import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { locales } from 'app/locales';
import { IntlProvider } from 'react-intl';

import createApolloClient from 'app/utils/apollo/client';

import GlobalStyle, { BaseGlobalStyle } from 'app/global-styles';
import { theme } from 'app/providers/theme';

import useConfig, { ConfigProvider, config } from 'app/config';
import useServices, { ServicesProvider } from 'app/services';

import type { TokenPayload } from 'app/services/auth';

import { refreshTokens, signout } from 'actions/auth';
import { AppRoutes } from 'app/routes/app-routes';
import { StateProvider, useAppState, useDispatch } from 'state';

import useAuthCode from 'app/hooks/use-auth-code';
import { GymStructureProvider } from 'app/providers/gym-structure';
import CookieConsentModal from 'app/ui/components/molecules/cookie-consent-modal';
import { AnalyticsProvider } from 'app/utils/analytics/provider';
import { useTimerEnabledListener } from 'src/app/hooks/use-timer-enabled-listener';
import { CommunicationProvider, useCommunicationService } from 'src/app/services/communication';
import { MessageType } from 'src/app/utils/in-app-comunication/types';
import Maintenance from 'app/pages/maintenance';

// Navigates to a URL when a 'whiteboard-open' message is received from window messages
const WindowMessageNavigator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const comms = useCommunicationService();

  useTimerEnabledListener();

  useEffect(() => {
    const unsubscribe = comms.onMessage((message) => {
      if (message.type === MessageType.WHITEBOARD_OPEN && message.url && location.pathname === '/') {
        navigate(message.url);
      }
    });
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [comms, navigate, location.pathname]);

  return null;
};

const PageWrapper = () => {
  const { config } = useConfig();
  const services = useServices();
  const dispatch = useDispatch();

  const { signedIn, expires } = useAppState(({ auth }) => ({
    signedIn: auth.signedIn,
    expires: auth.expires,
  }));

  // Get auth code when user is signed in (only executes in iframe)
  useAuthCode();

  useEffect(() => {
    if (signedIn && expires && expires <= new Date()) {
      dispatch(signout());
    }
  }, [signedIn, expires, dispatch]);

  const onTokenRefresh = useCallback(
    (payload: TokenPayload) => (payload.refreshToken ? dispatch(refreshTokens(payload)) : dispatch(signout())),
    [dispatch],
  );

  const onTokenRefreshError = useCallback(() => dispatch(signout()), [dispatch]);

  const { client } = useMemo(
    () =>
      createApolloClient({
        uri: config.REACT_APP_HYROX_API_URL,
        authService: services.auth,
        oneFiitAuthEnabled: Boolean(config.REACT_APP_ONE_FIIT_AUTH_URL),
        onTokenRefresh,
        onTokenRefreshError,
      }),
    [
      config.REACT_APP_HYROX_API_URL,
      config.REACT_APP_ONE_FIIT_AUTH_URL,
      services.auth,
      onTokenRefresh,
      onTokenRefreshError,
    ],
  );

  return (
    <ApolloProvider client={client}>
      <AppRoutes />
    </ApolloProvider>
  );
};

const AppWithLocale = () => {
  const languageCode = useAppState(({ language }) => language?.preferredLanguage?.code?.toLowerCase()) || 'en';

  // Language preference setting is now handled in the Home component with proper consent checks

  return (
    <IntlProvider defaultLocale="en" locale={languageCode} messages={locales[languageCode]}>
      <Router>
        <WindowMessageNavigator />
        <PageWrapper />
        <CookieConsentModal />
      </Router>
    </IntlProvider>
  );
};

const AppWithConfig = () => {
  const { config } = useConfig();

  useEffect(() => {
    if (config.REACT_APP_SENTRY_DSN) {
      Sentry.init({
        dsn: config.REACT_APP_SENTRY_DSN,
        environment: config.REACT_APP_SENTRY_ENVIRONMENT,
      });
    }
  }, [config.REACT_APP_SENTRY_DSN, config.REACT_APP_SENTRY_ENVIRONMENT]);

  return (
    <ThemeProvider theme={theme}>
      <StateProvider>
        <AnalyticsProvider config={config}>
          <ServicesProvider config={config}>
            <CommunicationProvider>
              <GymStructureProvider>
                <GlobalStyle />
                <AppWithLocale />
              </GymStructureProvider>
            </CommunicationProvider>
          </ServicesProvider>
        </AnalyticsProvider>
      </StateProvider>
    </ThemeProvider>
  );
};

// Maintenance mode is a hard stop, not a redirect. Nothing below AppWithConfig mounts,
// so there is no router, no Apollo client and no auth, state or analytics provider —
// the app makes no API calls and has no other tree to render, whatever URL is opened.
// Enforcement is still client side: it hides the UI, it does not protect the API.
const App = () =>
  config.REACT_APP_MAINTENANCE_MODE_ENABLED ? (
    <ThemeProvider theme={theme}>
      <BaseGlobalStyle />
      <Maintenance />
    </ThemeProvider>
  ) : (
    <ConfigProvider>
      <AppWithConfig />
    </ConfigProvider>
  );

const root = createRoot(document.getElementById('root') || document.createElement('div'));
root.render(<App />);
